import { InfoCard, sidebarConfig } from '@backstage/core-components';
import { EntityRefLinks } from '@backstage/plugin-catalog-react';
import { UserSettingsAppearanceCard, UserSettingsMenu } from '@backstage/plugin-user-settings';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { UserProfileProvider, UserProfileSessionCache, useUserProfile } from './UserProfileProvider';

const useAvatarStyles = makeStyles(theme => ({
  avatar: ({ size }: { size: number }) => ({
    width: size,
    height: size,
    fontSize: size * 0.7,
    border: `1px solid ${theme.palette.textSubtle}`,
  }),
}));

const CachedUserSettingsSignInAvatar = ({ size }: { size?: number }) => {
  const avatarSize = size ?? sidebarConfig.iconSize;
  const classes = useAvatarStyles({ size: avatarSize });
  const { profile } = useUserProfile();

  return <Avatar src={profile.picture} className={classes.avatar} alt="Profile picture" />;
};

const CachedUserSettingsProfileCard = () => {
  const { displayName, profile } = useUserProfile();

  return (
    <InfoCard title="Profile" variant="gridItem">
      <Grid container spacing={6}>
        <Grid item>
          <CachedUserSettingsSignInAvatar size={96} />
        </Grid>
        <Grid item xs={12} sm container>
          <Grid item xs container direction="column" spacing={2}>
            <Grid item xs>
              <Typography variant="subtitle1" gutterBottom>
                {displayName}
              </Typography>
              {profile.email && (
                <Typography variant="body2" color="textSecondary">
                  {profile.email}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <UserSettingsMenu />
          </Grid>
        </Grid>
      </Grid>
    </InfoCard>
  );
};

const CachedUserSettingsIdentityCard = () => {
  const { backstageIdentity, loading } = useUserProfile();

  const content = !backstageIdentity ? (
    <Typography>No Backstage Identity</Typography>
  ) : (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          User Entity: <EntityRefLinks entityRefs={[backstageIdentity.userEntityRef]} />
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1">
          Ownership Entities: <EntityRefLinks entityRefs={backstageIdentity.ownershipEntityRefs} />
        </Typography>
      </Grid>
    </Grid>
  );

  return <InfoCard title="Backstage Identity">{loading ? null : content}</InfoCard>;
};

const CachedUserSettingsGeneralContents = () => (
  <Grid container direction="row" spacing={3}>
    <Grid item xs={12} md={6}>
      <CachedUserSettingsProfileCard />
    </Grid>
    <Grid item xs={12} md={6}>
      <UserSettingsAppearanceCard />
    </Grid>
    <Grid item xs={12} md={6}>
      <CachedUserSettingsIdentityCard />
    </Grid>
  </Grid>
);

export const CachedUserSettingsGeneral = ({ cache }: { cache?: UserProfileSessionCache }) => (
  <UserProfileProvider cache={cache}>
    <CachedUserSettingsGeneralContents />
  </UserProfileProvider>
);
